/**
 * src/utils/text-measure-util.js
 *
 * 문자열 폭 측정 — canvas measureText 와 그 폴백, 폭 안에 맞춰 자르기.
 *
 * 표(fitColumns.js)와 flo 흐름도 렌더러가 이 측정을 함께 쓴다.
 * canvas 컨텍스트가 없는 환경(jsdom 등)에서는 글자 수 기반 추정으로 대신한다.
 */
import { getFontSizePx } from "@plannel/utils/font-util";

// 측정용 canvas 는 모듈 스코프 1개를 재사용한다 — 호출마다 만들면 GC 압력만 커진다.
let _measureCtx = null;
function measureCtx() {
  if (_measureCtx === null) {
    try {
      _measureCtx = document.createElement("canvas").getContext("2d") || false;
    } catch {
      _measureCtx = false;
    }
  }
  return _measureCtx || null;
}

/**
 * 문자열 폭(px) — canvas 2D 컨텍스트로 잰다.
 * jsdom 등 canvas 가 없는 환경에서는 글자 수 기반 추정으로 대신한다.
 */
export function measureText(s, font) {
  const ctx = measureCtx();
  // 글꼴 크기 토큰은 CSS 변수 문자열이라 산술이 안 된다 — 해석된 px 를 쓴다.
  if (!ctx) return String(s).length * getFontSizePx("body") * 0.6;
  ctx.font = font;
  return ctx.measureText(String(s)).width;
}

// 전각 문자(한글·한자·가나) — 측정이 안 될 때 폭 추정에서 1em 으로 센다.
const WIDE_CHAR = /[ᄀ-ᇿ⺀-鿿가-힯豈-﫿＀-￯]/;

/**
 * 문자열 폭(px) — measureText 를 쓰되, 결과가 유한하지 않으면(폰트 파싱 실패 등)
 * 전각·반각을 가른 글자별 추정으로 대신한다.
 */
export function textWidth(s, font) {
  const w = measureText(s, font);
  if (Number.isFinite(w)) return w;
  const px = parseFloat((/(\d+(?:\.\d+)?)px/.exec(String(font)) || [])[1]) || 12;
  return Array.from(String(s)).reduce((acc, ch) => acc + (WIDE_CHAR.test(ch) ? px : px * 0.6), 0);
}

/**
 * 문자열을 maxWidth 안에 맞춰 자르고 말줄임표(…)를 붙인다.
 * 이진 탐색으로 잘라도 되는 가장 긴 자리를 찾는다.
 */
export function truncateToWidth(text, maxWidth, font) {
  const s = String(text ?? "");
  if (!s || maxWidth <= 0) return "";
  if (textWidth(s, font) <= maxWidth) return s;
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (textWidth(`${s.slice(0, mid)}…`, font) <= maxWidth) lo = mid; else hi = mid - 1;
  }
  return lo > 0 ? `${s.slice(0, lo)}…` : "…";
}
